import { IOption } from '../../base/base-forms/index.ts';
import {
    JobApplicationStatuses,
    JobApplicationStatusUpdateValues,
    JobApplicationTrailActionTypes,
    JobApplicationTrailRelatedObjectTypes,
} from '../consts/jobApplication.consts.ts';
import { IBaseServerEntity } from './base.interfaces';
import {
    IServerJob,
    IServerApplyQuestionResponse,
    IApplyQuestion,
} from './job.interfaces.ts';
import {
    IServerUser,
    IExtendedBasicUser,
    IBaseUser,
} from './user.interfaces.ts';
import { ITag } from './organization.interfaces';
import { Moment } from 'moment';

export enum ApplyNowFormValues {
    firstName = 'firstName',
    lastName = 'lastName',
    phoneNumber = 'phoneNumber',
    email = 'email',
    file = 'file',
    acceptTerms = 'acceptTerms',
    isReferrer = 'isReferrer',
    referrerName = 'referrerName',
    referrerLastName = 'referrerLastName',
    referrerEmail = 'referrerEmail',
    referrerPhoneNumber = 'referrerPhoneNumber',
    referrerCompany = 'referrerCompany',
    coverLetter = 'coverLetter',
    jobApplicationId = 'jobApplicationId',
    questionAnswers = 'questionAnswers',
}

export interface IApplyNowForm {
    [ApplyNowFormValues.firstName]: string;
    [ApplyNowFormValues.lastName]: string;
    [ApplyNowFormValues.phoneNumber]: string;
    [ApplyNowFormValues.email]: string;
    [ApplyNowFormValues.file]: File | null;
    [ApplyNowFormValues.acceptTerms]: boolean;
    [ApplyNowFormValues.isReferrer]?: boolean;
    [ApplyNowFormValues.referrerName]?: string;
    [ApplyNowFormValues.referrerLastName]?: string;
    [ApplyNowFormValues.referrerEmail]?: string;
    [ApplyNowFormValues.referrerPhoneNumber]?: string;
    [ApplyNowFormValues.referrerCompany]?: string;
    [ApplyNowFormValues.coverLetter]?: string;
    [ApplyNowFormValues.jobApplicationId]?: IOption | null;
}

export interface IApplyNowCandidateInformationForm {
    [ApplyNowFormValues.firstName]: string;
    [ApplyNowFormValues.lastName]: string;
    [ApplyNowFormValues.phoneNumber]: string;
    [ApplyNowFormValues.email]: string;
    [ApplyNowFormValues.file]: File | null;
    [ApplyNowFormValues.isReferrer]?: boolean;
    [ApplyNowFormValues.jobApplicationId]?: IOption | null;
}

export interface IApplyNowReferrerInformationForm {
    [ApplyNowFormValues.referrerName]: string;
    [ApplyNowFormValues.referrerLastName]: string;
    [ApplyNowFormValues.referrerEmail]: string;
    [ApplyNowFormValues.referrerPhoneNumber]: string;
    [ApplyNowFormValues.referrerCompany]: string;
    [ApplyNowFormValues.coverLetter]?: string;
}

export enum QuestionAnswerFields {
    QUESTION_ID = 'questionId',
    ANSWER = 'answer',
}

export interface IDateRangeAnswer {
    from: Date | null;
    to: Date | null;
}

export type TQuestionAnswer =
    | string
    | number
    | IDateRangeAnswer
    | Date
    | number[]
    | File
    | null;

export interface IQuestionAnswerFormValue {
    [QuestionAnswerFields.QUESTION_ID]: number;
    [QuestionAnswerFields.ANSWER]: TQuestionAnswer;
}

export interface IApplyNowAdditionalQuestionsForm {
    [ApplyNowFormValues.acceptTerms]: boolean;
    [ApplyNowFormValues.questionAnswers]: IQuestionAnswerFormValue[];
}

export interface IQuestionAnswerPayload {
    apply_question: number;
    answer?: string;
    selected_option_values?: number[];
    file?: File;
}

export enum RejectApplicationFormValues {
    reasonOption = 'reasonOption',
    reasonText = 'reasonText',
}

export interface IRejectApplicationForm {
    [RejectApplicationFormValues.reasonOption]: IOption | null;
    [RejectApplicationFormValues.reasonText]: string | null;
}

interface IServerApplicationCandidate {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    image?: string;
}
interface IServerApplicationJob {
    id: number;
    title: string;
}

export interface IServerApplication extends IBaseServerEntity {
    id: number;
    job: IServerApplicationJob;
    status: string;
    submission_date: string;
    candidate_score: number;
    current_step: number;
    is_saved: boolean;
    is_legacy: boolean;
    tags: Array<ITag>;
    referral_source: string | null;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
}

export type UserDecision = 'pass' | 'reject' | null;

export interface IStepUser {
    id: string;
    name: string;
    avatar: string;
    decision: UserDecision;
    isOwner: boolean;
}

export interface IStepData {
    id: number;
    name: string;
    status?: JobApplicationStatusUpdateValues;
    lifecycle_status?: string;
    users: Array<IStepUser>;
    interview: Array<IStepInterview>;
}

export interface IJobApplicationComment {
    id: number;
    user: IServerUser;
    date: string;
    text: string;
}

export interface IJobApplicationRequirement {
    id: number;
    name: string;
    score: number;
    reason: string;
    job_application_score?: number;
    user_requirement_scores?: Array<IJobApplicationUserRequirementScore>;
}

export interface IServerJobApplicationRequirementCategory {
    importance_level: string;
    requirements: Array<IJobApplicationRequirement>;
}

export type IRequirementsGrouped = {
    [importanceLevel: string]: Array<IJobApplicationRequirement>;
};

export type IRequirementsData = {
    [sourceKey: string]: IRequirementsGrouped;
};

export interface IClientInsights {
    positiveInsights: Array<string>;
    negativeInsights: Array<string>;
}

export interface IServerInsights {
    positive_insights: Array<{ insight: string }>;
    negative_insights: Array<{ insight: string }>;
}

export interface IServerQuestionAnswer {
    id: number;
    apply_question: IServerApplyQuestionResponse;
    answer?: string;
    selected_options?: Array<IOption>;
    file_url?: string;
    file_name?: string;
    created_at: string;
}

export interface IServerJobApplication {
    id: number;
    candidate_score: number;
    comments: Array<IJobApplicationComment>;
    insights: IServerInsights;
    is_saved: boolean;
    job: IServerJob;
    status: JobApplicationStatuses;
    submission_date: string;
    requirements: IRequirementsData;
    resume_url: string;
    steps: Array<IStepData>;
    current_step: number;
    referrer: {
        user: IServerUser;
        company_name: string;
        is_point_of_contact: boolean;
    };
    referral_source: string;
    notes?: string;
    tags: Array<ITag>;
    trail: Array<IServerJobApplicationTrail>;
    chat: Array<IServerJobApplicationChatMessage>;
    question_answers?: Array<IServerQuestionAnswer>;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
}

export interface IJobApplicationRequirementsCategory {
    importanceLevel: string;
    requirements: Array<IJobApplicationRequirement>;
}

export interface IQuestionAnswer {
    id: number;
    applyQuestion: IApplyQuestion;
    answer?: string;
    selectedOptions?: Array<IOption>;
    fileUrl?: string;
    fileName?: string;
    createdAt: string;
}

export interface IJobApplicationJob {
    id: number;
    location: string;
    title: string;
    JobType: string;
}

export interface IJobApplication {
    id: number;
    candidateScore: number;
    job: IJobApplicationJob;
    resumeUrl: string;
    status: JobApplicationStatuses;
    submissionDate: string;
    insights: IClientInsights;
    comments: Array<IJobApplicationComment>;
    requirements: IRequirementsData;
    isSaved: boolean;
    steps: Array<IStepData>;
    currentStep: number;
    source?: string;
    referrer?: IReferrer;
    notes?: string;
    tags: Array<ITag>;
    trail: Array<IServerJobApplicationTrail>;
    chat: Array<IServerJobApplicationChatMessage>;
    emails: Array<IServerJobApplicationEmail>;
    questionAnswers?: Array<IQuestionAnswer>;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    isLegacy: boolean;
}

export interface IReferrer {
    user: IExtendedBasicUser;
    companyName: string;
    isPointOfContact: boolean;
}

export interface IInterviewQuestion {
    id: number;
    question: string;
    guidelines: string;
    answer: string;
    insights: string;
    is_ai_generated: boolean;
}

export interface ICalendarEvent {
    id: number;
    event_id: string;
    type: string;
    mailbox: string;
}

export interface IStepInterview {
    id: number;
    job_application: number;
    interviewers: IServerApplicationCandidate[];
    step: number;
    calendar_event?: ICalendarEvent;
    interview_questions: IInterviewQuestion[];
}

export interface IJobApplicationUserRequirementScore {
    id: number;
    score: number;
    reason: string;
    job_application_score: number;
    user: IBaseUser;
}

export interface IServerJobApplicationTrailParams {
    // COMMENT
    comment?: string;
    // STAGE_CHANGE
    original_step?: string;
    new_step?: string;
    // STATUS_CHANGE
    from_status?: JobApplicationStatuses;
    to_status?: JobApplicationStatuses;
    changed_at?: string;
    // REJECTED
    reject_reason?: string;
    reject_comment?: string;
    // NOTE_ADDED
    original_notes?: string;
    new_notes?: string;
    // INTERVIEW_SCHEDULED, INTERVIEW_UPDATED, INTERVIEW_DELETED
    event_id?: string;
    // LIFECYCLE_CHANGE
    original_status?: string;
    new_status?: string;
    step_id?: number;
    step_name?: string;
}

export interface IServerJobApplicationTrailRelatedObject {
    id: number;
    type: JobApplicationTrailRelatedObjectTypes;
    // Comment fields
    text?: string;
    date?: string;
    step?: number;
    user?: IBaseUser;
    // Step fields
    name?: string;
    status?: string;
    users?: Array<IStepUser>;
    interview?: Array<IStepInterview>;
}
export interface IServerJobApplicationRelatedUser {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    image: string;
}

export interface IServerJobApplicationTrail {
    id: number;
    user: IServerJobApplicationRelatedUser | null;
    action_type: JobApplicationTrailActionTypes;
    params: IServerJobApplicationTrailParams;
    related_object: IServerJobApplicationTrailRelatedObject;
    created_at: string;
}

export interface IServerJobApplicationChatMessage {
    id: number;
    message: string;
    user: IServerJobApplicationRelatedUser;
    created_at: string;
}

export enum ScheduleInterviewFormFields {
    date = 'date',
    name = 'name',
    timeFrom = 'timeFrom',
    timeTo = 'timeTo',
    description = 'description',
}

export interface IScheduleInterviewForm {
    [ScheduleInterviewFormFields.date]: Moment;
    [ScheduleInterviewFormFields.name]: string;
    [ScheduleInterviewFormFields.timeFrom]: Moment;
    [ScheduleInterviewFormFields.timeTo]: Moment;
    [ScheduleInterviewFormFields.description]: string | null;
}

export interface IServerJobApplicationEmail {
    id: number;
    subject: string;
    body: string;
    files: Array<IServerJobApplicationEmailFile>;
    user: IServerJobApplicationRelatedUser;
    carbon_copy: Array<IServerJobApplicationRelatedUser>;
    created_at: string;
}

export interface IServerJobApplicationEmailFile {
    id: number;
    file_url: string;
    file_name: string;
    size: number;
}

export enum SendEmailFormFields {
    to = 'to',
    cc = 'cc',
    bcc = 'bcc',
    from = 'from',
    subject = 'subject',
    body = 'body',
}

export interface ISendEmailForm {
    [SendEmailFormFields.to]: string;
    [SendEmailFormFields.cc]: string[];
    [SendEmailFormFields.bcc]: string[];
    [SendEmailFormFields.from]: string;
    [SendEmailFormFields.subject]: string;
    [SendEmailFormFields.body]: string;
}

export interface ITimeWindow {
    id: string;
    from: string;
    to: string;
}

export type IInterviewAvailability = Record<number, ITimeWindow[]>;

export interface IScheduleInterviewData {
    interviewDuration: IOption;
    availability: IInterviewAvailability;
}

export interface ITimeSlotItem {
    from_hour: number;
    from_minutes: number;
    to_hour: number;
    to_minutes: number;
}

export type IInterviewTimeslots = Record<string, ITimeSlotItem[]>;

export interface IScheduleInterviewApiPayload {
    duration: number;
    timeslots: IInterviewTimeslots;
}
